import { useCallback, useRef } from 'react';

import type { AvailableSite } from '@atlassian/gic-anywhere';
import { useGetFirstAvailableSiteDomain } from '@atlassian/gic-anywhere';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { fg } from '@confluence/feature-gating';
import { expVal } from '@confluence/feature-experiments';

import {
	ErrorStates,
	useSetManualCreateState,
	useSetError,
} from '../providers/BulkCreateContextProvider.tsx';
import {
	useAvailableSitesActions,
	useAvailableSitesState,
} from '../providers/BulkConfigureContextProvider.tsx';
import {
	getPersistedOverrideCloudId,
	clearPersistedOverrideCloudId,
} from '../utils/localStorageHelpers.ts';
import { useBulkCreateLoggingEvents } from '../utils/useBulkCreateLoggingEvents.tsx';

export const useGetAvailableSites = () => {
	const { setSelectedAvailableSite, setOptionsAvailableSites } = useAvailableSitesActions();
	const { selectedAvailableSite } = useAvailableSitesState();
	const { cloudId: sessionCloudId } = useSessionData();

	const persistedOverrideCloudId = getPersistedOverrideCloudId();

	// Use the persisted cloudId preference if available, otherwise fall back to the current
	// Confluence session cloudId. Note: sessionCloudId is the Confluence site's cloud ID and
	// may not have a Jira license — the onComplete callback validates this against available
	// Jira sites and falls back to the first valid Jira site if needed.
	const overrideCloudIdForSitePicker = persistedOverrideCloudId || sessionCloudId;
	const { logRequestFailed } = useBulkCreateLoggingEvents();
	const setError = useSetError();
	const setManualCreateState = useSetManualCreateState();

	const selectedAvailableSiteRef = useRef(selectedAvailableSite);
	selectedAvailableSiteRef.current = selectedAvailableSite;

	useGetFirstAvailableSiteDomain({
		preferredCloudId: overrideCloudIdForSitePicker,
		onComplete: useCallback(
			({ availableSites }: { availableSites: AvailableSite[] }) => {
				const firstSite = availableSites[0];
				const preferredSite = availableSites.find(
					(site: AvailableSite) => site.cloudId === overrideCloudIdForSitePicker,
				);

				// If the preferred cloudId (persisted or session) is not found in the list of
				// Jira-licensed sites, clear the persisted preference so it is not reused on
				// subsequent opens, and fall back to the first available Jira site.
				if (
					!preferredSite &&
					persistedOverrideCloudId &&
					expVal('confluence-jwc-remove-sites-without-jira-exp', 'isEnabled', false)
				) {
					clearPersistedOverrideCloudId();
				}

				const newSite = preferredSite || firstSite;
				// Only update the selected site if it has changed to avoid resetting downstream selections
				if (
					selectedAvailableSiteRef.current?.cloudId !== newSite?.cloudId ||
					!fg('confluence-create-work-items-from-action-items')
				) {
					void setSelectedAvailableSite(newSite);
				}
				void setOptionsAvailableSites(availableSites);
			},
			[
				setSelectedAvailableSite,
				setOptionsAvailableSites,
				overrideCloudIdForSitePicker,
				persistedOverrideCloudId,
			],
		),
		onError: useCallback(
			(error: Error) => {
				logRequestFailed({ requestName: 'fetch-jira-site', errorMessage: error.message });
				void setError(ErrorStates.SITE_RETRIEVAL);
				void setManualCreateState('error');
			},
			[logRequestFailed, setError, setManualCreateState],
		),
	});
};
